/**
 * @license Copyright (c) 2003-2020, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import ClassicEditorBase from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import UploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder';
// import EasyImage from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import Base64UploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/base64uploadadapter';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import Link from '@ckeditor/ckeditor5-link/src/link';
import List from '@ckeditor/ckeditor5-list/src/list';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation';

import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Subscript from '@ckeditor/ckeditor5-basic-styles/src/subscript';
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import Code from '@ckeditor/ckeditor5-basic-styles/src/code';
import Font from '@ckeditor/ckeditor5-font/src/font';
import SpecialCharacters from '@ckeditor/ckeditor5-special-characters/src/specialcharacters';
import SpecialCharactersLatin from '@ckeditor/ckeditor5-special-characters/src/specialcharacterslatin';
import SpecialCharactersMath from '@ckeditor/ckeditor5-special-characters/src/specialcharactersmathematical';
import SpecialCharactersText from '@ckeditor/ckeditor5-special-characters/src/specialcharacterstext';
import Highlight from '@ckeditor/ckeditor5-highlight/src/highlight';

function SpecialCharactersGreek( editor ) {
	editor.plugins.get( 'SpecialCharacters' ).addItems( 'Greek', [
		{ character: 'Α', title: 'Greek Capital Letter Alpha' },
		{ character: 'α', title: 'Greek Small Letter alpha' },
		{ character: 'Β', title: 'Greek Capital Letter Beta' },
		{ character: 'β', title: 'Greek Small Letter beta' },
		{ character: 'Γ', title: 'Greek Capital Letter Gamma' },
		{ character: 'γ', title: 'Greek Small Letter gamma' },
		{ character: 'Δ', title: 'Greek Capital Letter Delta' },
		{ character: 'δ', title: 'Greek Small Letter delta' },
		{ character: 'Ε', title: 'Greek Capital Letter Epsilon' },
		{ character: 'ε', title: 'Greek Small Letter epsilon' },
		{ character: 'Ζ', title: 'Greek Capital Letter Zeta' },
		{ character: 'ζ', title: 'Greek Small Letter zeta' },
		{ character: 'Η', title: 'Greek Capital Letter Eta' },
		{ character: 'η', title: 'Greek Small Letter eta' },
		{ character: 'Θ', title: 'Greek Capital Letter Theta' },
		{ character: 'θ', title: 'Greek Small Letter theta' },
		{ character: 'Ι', title: 'Greek Capital Letter Iota' },
		{ character: 'ι', title: 'Greek Small Letter iota' },
		{ character: 'Κ', title: 'Greek Capital Letter Kappa' },
		{ character: 'κ', title: 'Greek Small Letter kappa' },
		{ character: 'Λ', title: 'Greek Capital Letter Lambda' },
		{ character: 'λ', title: 'Greek Small Letter lambda' },
		{ character: 'Μ', title: 'Greek Capital Letter Mu' },
		{ character: 'μ', title: 'Greek Small Letter mu' },
		{ character: 'Ν', title: 'Greek Capital Letter Nu' },
		{ character: 'ν', title: 'Greek Small Letter nu' },
		{ character: 'Ξ', title: 'Greek Capital Letter Xi' },
		{ character: 'ξ', title: 'Greek Small Letter xi' },
		{ character: 'Ο', title: 'Greek Capital Letter Omicron' },
		{ character: 'ο', title: 'Greek Small Letter omicron' },
		{ character: 'Π', title: 'Greek Capital Letter Pi' },
		{ character: 'π', title: 'Greek Small Letter pi' },
		{ character: 'Ρ', title: 'Greek Capital Letter Rho' },
		{ character: 'ρ', title: 'Greek Small Letter rho' },
		{ character: 'Σ', title: 'Greek Capital Letter Sigma' },
		{ character: 'σ', title: 'Greek Small Letter sigma' },
		{ character: 'Τ', title: 'Greek Capital Letter Tau' },
		{ character: 'τ', title: 'Greek Small Letter tau' },
		{ character: 'Υ', title: 'Greek Capital Letter Upsilon' },
		{ character: 'υ', title: 'Greek Small Letter upsilon' },
		{ character: 'Φ', title: 'Greek Capital Letter Phi' },
		{ character: 'φ', title: 'Greek Small Letter phi' },
		{ character: 'Χ', title: 'Greek Capital Letter Chi' },
		{ character: 'χ', title: 'Greek Small Letter chi' },
		{ character: 'Ψ', title: 'Greek Capital Letter Psi' },
		{ character: 'ψ', title: 'Greek Small Letter psi' },
		{ character: 'Ω', title: 'Greek Capital Letter Omega' },
		{ character: 'ω', title: 'Greek Small Letter omega' }
	] );
}

export default class ClassicEditor extends ClassicEditorBase {}

// Plugins to include in the build.
ClassicEditor.builtinPlugins = [
	Essentials,
	UploadAdapter,
	Autoformat,
	Bold,
	Italic,
	BlockQuote,
	CKFinder,
	// EasyImage,
	Heading,
	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Base64UploadAdapter,
	Indent,
	Link,
	List,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	Table,
	TableToolbar,
	TextTransformation,
	Alignment,
	Underline,
	Subscript,
	Superscript,
	Strikethrough,
	Code,
	Font,
	SpecialCharacters,
	SpecialCharactersLatin,
	SpecialCharactersMath	,
	SpecialCharactersGreek,
	SpecialCharactersText,
	Highlight,
];

// Editor configuration.
ClassicEditor.defaultConfig = {
	toolbar: {
		items: [
			'undo',
			'redo',
			'|',
			'heading',
			'|',
			'bold',
			'italic',
			'underline',
			'strikethrough',
			'|',
			'alignment',
			'|',
			'bulletedList',
			'numberedList',
			'|',
			'subscript',
			'superscript',
			'code',
			'blockQuote',
			'|',
			'specialCharacters',
			'imageUpload',
			'insertTable',
			'|',
			'fontFamily',
			'fontSize',
			'fontColor',
			'FontBackgroundColor',
			'highlight',
		]
	},
	image: {
		toolbar: [
			'imageStyle:full',
			'imageStyle:side',
			'|',
			'imageTextAlternative'
		]
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells'
		]
	},
	// This value must be kept in sync with the language defined in webpack.config.js.
	language: 'en'
};
